import React, { type ReactNode, useEffect } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { Stack, Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';

import { useAnalytics } from '../../../common/ui/analytics';

import messages from './messages';
import type { ErrorPanelProps } from './types';

const bold = (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>;

const WarningText = ({ children }: { children: ReactNode }) => (
	<Text size="medium" color="color.text.warning">
		{children}
	</Text>
);

export const MAX_INVITES = 10;

const Message = ({
	successEmails,
	errorEmails,
	exceedsMaxInvites,
	endpointFailure,
}: Omit<ErrorPanelProps, 'integration'>) => {
	if (exceedsMaxInvites) {
		return (
			<WarningText>
				<FormattedMessage {...messages.maxInvites} values={{ max: MAX_INVITES }} />
			</WarningText>
		);
	}

	// Show this message if the endpoint fails or if there are no successful invites
	if (endpointFailure || successEmails.length === 0) {
		return (
			<WarningText>
				<FormattedMessage
					{...messages.error}
					values={{ count: successEmails.length + errorEmails.length }}
				/>
			</WarningText>
		);
	}

	return (
		<Stack space="space.100">
			<WarningText>
				<FormattedMessage
					{...messages.partialSuccess}
					values={{
						count: successEmails.length,
						email: successEmails[0],
						others: successEmails.length - 1,
						bold,
					}}
				/>
			</WarningText>
			<WarningText>
				<FormattedMessage
					{...messages.partialError}
					values={{
						count: errorEmails.length,
						bold,
					}}
				/>
			</WarningText>
		</Stack>
	);
};

export const ErrorPanel = ({ integration, ...props }: ErrorPanelProps): React.JSX.Element => {
	const fireAnalyticsEvent = useAnalytics();

	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'inlineInviteError',
			attributes: { integration },
		});
	}, [fireAnalyticsEvent, integration]);

	return (
		<SectionMessage appearance="warning">
			<Message {...props} />
		</SectionMessage>
	);
};
